// src/pages/UtentiPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/* =============== API =============== */
const API_BASE = "/preventivi/api";
const LIST_URL = `${API_BASE}/users_list.php`;
const SAVE_URL = `${API_BASE}/users_save.php`;
const DEL_URL  = `${API_BASE}/users_delete.php`;
const SAVE_HOME_URL = `${API_BASE}/users_save_home.php`;
const GEOCODE_URL   = `${API_BASE}/geocode_address.php`;

/* =============== COSTANTI =============== */
const ROLES = [
  { value: "",             label: "Tutti" },
  { value: "centralino",   label: "Centralino" },
  { value: "formatore",    label: "Formatore" },
  { value: "rilevatore",   label: "Rilevatore" },
  { value: "senior",       label: "Senior" },
  { value: "senior_posa",  label: "Senior Posa" }, // ← deve esserci
  { value: "ospite",       label: "Ospite" },      // ← opzionale
  { value: "responsabile", label: "Responsabile di zona" },
  { value: "admin",        label: "Admin" },
];

const ZONE = [
  { id: 1, label: "Verona" },
  { id: 2, label: "Modena" },
  { id: 3, label: "Novara" },
  { id: 4, label: "Milano Sud" },
  { id: 5, label: "Milano Nord" },
];

const ROLE_COLORS = {
  centralino:   { bg:"#0ea5e9", soft:"#e0f2fe" },
  formatore:    { bg:"#a855f7", soft:"#f3e8ff" },
  rilevatore:   { bg:"#22c55e", soft:"#dcfce7" },
  senior:       { bg:"#f59e0b", soft:"#fef3c7" },
  // come Senior a livello colore primario; "soft" più scuro per distinguere a colpo d'occhio negli avatar
  senior_posa:  { bg:"#f59e0b", soft:"#013220" },
  ospite:       { bg:"#64748b", soft:"#e2e8f0" },
  responsabile: { bg:"#06b6d4", soft:"#cffafe" },
  admin:        { bg:"#ef4444", soft:"#fee2e2" },
  default:      { bg:"#64748b", soft:"#e2e8f0" },
};

/* =============== HELPERS HTTP =============== */
async function apiGet(u, signal){
  const r = await fetch(u, { credentials:"same-origin", signal });
  const j = await r.json().catch(() => ({}));
  if(!r.ok) throw new Error(j.message || `HTTP ${r.status}`);
  return j;
}

/*  ✅ versione che mostra l’errore vero anche su HTTP 500  */
async function apiPost(u, b){
  const r = await fetch(u, {
    method:"POST",
    headers:{"Content-Type":"application/json"},
    credentials:"same-origin",
    body: JSON.stringify(b)
  });
  const txt = await r.text();         // leggiamo sempre il corpo
  let j; try { j = JSON.parse(txt); } catch { j = null; }

  if (!r.ok) {
    const msg = (j && j.message) || txt || `HTTP ${r.status}`;
    throw new Error(msg);
  }
  if (j && j.status && j.status !== "OK") {
    throw new Error(j.message || "Errore salvataggio");
  }
  return j || { status: "OK" };
}

/* =============== ALTRE UTILITY =============== */
const pad2 = (n) => String(n).padStart(2,"0");
const todayYMD = () => {
  const d=new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
};
const zLabel = (id) => ZONE.find(z => z.id === Number(id))?.label || "—";
const initials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).slice(0,2);
  return parts.map(p => p[0]?.toUpperCase()).join("") || "U";
};
const roleLabel = (slug = "") => {
  const s = String(slug).trim().toLowerCase();
  return ROLES.find(r => r.value === s)?.label || (s ? s[0].toUpperCase() + s.slice(1) : "—");
};
const toIsoDate = (v) =>
  v && /^\d{2}\/\d{2}\/\d{4}$/.test(v) ? v.split("/").reverse().join("-") : (v || null);
const toDot = (v) =>
  v != null && v !== "" ? String(v).replace(/\s+/g, "").replace(",", ".") : null;

const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

/** Parse “soft” di un indirizzo “Via 12, 37121 Verona” → { street, cap, city } */
function parseHomeAddress(addr = "") {
  const clean = String(addr || "")
    .replace(/[,;|-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!clean) return { street:"", cap:"", city:"" };

  // Trova l’ULTIMO CAP a 5 cifre per gestire numeri civici con cifre
  const capMatches = [...clean.matchAll(/\b(\d{5})\b/g)];
  const capMatch = capMatches.length ? capMatches[capMatches.length - 1] : null;

  if (!capMatch) {
    // Nessun CAP: prova a spezzare sull’ultimo spazio → street ~ tutto, city vuota
    return { street: clean, cap:"", city:"" };
  }
  const cap = capMatch[1];
  const capIndex = capMatch.index ?? clean.indexOf(cap);

  const before = clean.slice(0, capIndex).trim();
  const after  = clean.slice(capIndex + 5).trim(); // dopo il CAP dovrebbe esserci il Comune
  const street = before.replace(/\s+/g, " ").trim();
  const city   = after.replace(/\s+/g, " ").trim();

  return { street, cap, city };
}

/** CSV helper */
function toCsv(rows = []) {
  const headers = [
    "ID","Nome","Email","Ruolo","Zona","Attivo",
    "Telefono","Email personale","Targa auto","Computer",
    "Indirizzo","Lat","Lng"
  ];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[;\n"]/.test(s)) return `"${s.replace(/"/g,'""')}"`;
    return s;
  };
  const lines = [headers.join(";")];
  for (const r of rows) {
    const zona = r.zona_nome || zLabel(r.zona_id);
    const prof = r.profilo || {};
    lines.push([
      r.id,
      r.nome || "",
      r.email || "",
      String(r.ruolo || ""),
      zona || "",
      Number(r.attivo) ? "Sì" : "No",
      (prof.telefono || ""),
      (prof.email_personale || ""),
      (prof.auto_targa || ""),
      (prof.computer || ""),
      (r.home_address || ""),
      r.home_lat ?? "",
      r.home_lng ?? "",
    ].map(esc).join(";"));
  }
  return "\ufeff" + lines.join("\n"); // BOM UTF-8 per Excel
}

/* =============== STORAGE =============== */
const LS_KEY = "UTENTI_PAGE_STATE_V1"; // filtri + sort persistenti

function loadUiState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function saveUiState(partial) {
  try {
    const prev = loadUiState() || {};
    localStorage.setItem(LS_KEY, JSON.stringify({ ...prev, ...partial }));
  } catch {}
}







/* =============== MODALE =============== */
function Modal({ open, title, onClose, width = 1040, children }) {
  const panelRef = React.useRef(null);

  // NEW: tieni una ref stabile verso l'ultima onClose
  const onCloseRef = React.useRef(onClose);
  React.useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  React.useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.(); // usa la ref
    };
    window.addEventListener("keydown", onKey);

    // autofocus solo quando SI APRE la modale
    const id = setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const el =
        root.querySelector("[data-autofocus]") ||
        root.querySelector("input, select, textarea, button");
      el?.focus?.();
    }, 0);

    return () => { window.removeEventListener("keydown", onKey); clearTimeout(id); };
  }, [open]); // ← onClose NON è più nelle dipendenze

  if (!open) return null;
  return (
    <div style={S.backdrop} onMouseDown={onClose}>
      <div
        style={{ ...S.modal, width: `min(${width}px,96vw)` }}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={S.modalHead}>
          <strong style={{ fontSize: 18 }}>{title}</strong>
          <button style={S.iconBtn} aria-label="Chiudi" onClick={onClose}>×</button>
        </div>
        <div style={{ padding: 18 }} ref={panelRef}>
          {children}
        </div>
      </div>
    </div>
  );
}





/* ======================================================
   PAGINA
   ====================================================== */
export default function UtentiPage(){
  const [loading,setLoading] = useState(false);
  const [rows,setRows]       = useState([]);
  const [err,setErr]         = useState("");

  const uiState = loadUiState() || {};
  const [role,setRole] = useState(uiState.role ?? "");
  const [zone,setZone] = useState(uiState.zone ?? 0);
  const [q,setQ]       = useState(uiState.q ?? "");
  const searchRef      = useRef(null);

  const [sortBy, setSortBy]   = useState(uiState.sortBy ?? "nome");
  const [sortDir, setSortDir] = useState(uiState.sortDir ?? "asc"); // asc|desc

  const [open,setOpen] = useState(false);
  const [form,setForm] = useState(makeEmptyForm());

  /* scorciatoie tastiera */
  useEffect(() => {
    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toUpperCase();
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (isTypingTarget(e.target) || open) return;
      if (e.key === "/" && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }
      if (e.key.toLowerCase() === "n" && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        openNew();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* load con AbortController */
  useEffect(()=>{
    let abort = new AbortController();
    const load = async ()=>{
      try{
        setLoading(true); setErr("");
        const data = await apiGet(LIST_URL, abort.signal);
        setRows(Array.isArray(data.rows) ? data.rows : (data.users || []));
      }catch(e){
        if (e.name === "AbortError") return;
        setErr(e.message || "Errore caricamento");
      }finally{
        setLoading(false);
      }
    };
    load();
    return ()=> abort.abort();
  },[]);

  /* persistenza filtri/sort */
  useEffect(()=>{ saveUiState({ role, zone, q }); },[role,zone,q]);
  useEffect(()=>{ saveUiState({ sortBy, sortDir }); },[sortBy,sortDir]);

  /* filtro */
  const filtered = useMemo(()=>{
    const needle = q.trim().toLowerCase();
    return (rows||[]).filter(r=>{
      if (role && String(r.ruolo||"").toLowerCase() !== role) return false;
      if (zone && Number(r.zona_id||0) !== Number(zone)) return false;
      if (needle){
        const hay = [
          r.nome, r.email, r.ruolo, r.zona_nome || zLabel(r.zona_id),
          r.profilo?.telefono, r.profilo?.email_personale, r.home_address
        ].filter(Boolean).join(" ").toLowerCase();
        if(!hay.includes(needle)) return false;
      }
      return true;
    });
  },[rows,role,zone,q]);

  /* ordinamento */
  const sorted = useMemo(()=>{
    const arr = [...filtered];
    const dir = (sortDir === "desc" ? -1 : 1);
    arr.sort((a,b)=>{
      let va, vb;
      switch (sortBy) {
        case "id":      va = Number(a.id); vb = Number(b.id); break;
        case "email":   va = String(a.email||"").toLowerCase(); vb = String(b.email||"").toLowerCase(); break;
        case "ruolo":   va = String(a.ruolo||"").toLowerCase(); vb = String(b.ruolo||"").toLowerCase(); break;
        case "zona":    va = String(a.zona_nome || zLabel(a.zona_id) || "").toLowerCase();
                        vb = String(b.zona_nome || zLabel(b.zona_id) || "").toLowerCase(); break;
        case "attivo":  va = Number(a.attivo); vb = Number(b.attivo); break;
        default:        // nome
                        va = String(a.nome||"").toLowerCase();
                        vb = String(b.nome||"").toLowerCase();
      }
      if (va === vb) return 0;
      return (va > vb ? 1 : -1) * dir;
    });
    return arr;
  },[filtered, sortBy, sortDir]);

  const setSort = (key) => {
    if (sortBy === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  /* reload esplicito */
  const reload = async ()=>{
    try{
      setLoading(true); setErr("");
      const data = await apiGet(LIST_URL);
      setRows(Array.isArray(data.rows) ? data.rows : (data.users || []));
    }catch(e){
      setErr(e.message || "Errore caricamento");
    }finally{
      setLoading(false);
    }
  };

  /* open/mod save */
  const openNew = ()=>{
    setForm(makeEmptyForm());
    setOpen(true);
  };

  const openEdit=(r)=>{
    const roleSlug = String(r.ruolo ?? "centralino").toLowerCase();
    const base = {
      id: Number(r.id),
      nome: r.nome || "",
      email: r.email || "",
      password: "", // in modifica non precompilare password
      ruolo: roleSlug,
      zona_id: Number(r.zona_id || 0) || 0,
      attivo: Number(r.attivo ?? 1) ? 1 : 0,
      home_address: r.home_address || "",
      // gestione robusta di null/"" → input controllati
      home_lat: r.home_lat == null ? "" : String(r.home_lat),
      home_lng: r.home_lng == null ? "" : String(r.home_lng),
      profilo:{
        telefono: r.profilo?.telefono || "",
        foto: r.profilo?.foto || "",
        data_assunzione: r.profilo?.data_assunzione || "",
        data_scadenza_contratto: r.profilo?.data_scadenza_contratto || "",
        auto_targa: r.profilo?.auto_targa || "",
        computer: r.profilo?.computer || "",
        email_personale: r.profilo?.email_personale || "",
        plaud_assegnato: r.profilo?.plaud_assegnato || "",
        note: r.profilo?.note || "",
      }
    };

    // Parsing soft dell’indirizzo esistente
    const parsed = parseHomeAddress(base.home_address);
    setForm({
      ...base,
      home_street: parsed.street,
      home_cap: parsed.cap,
      home_city: parsed.city,
    });
    setOpen(true);
  };

  /* ---------- SUBMIT ---------- */
  const validateForm = (f) => {
    if (!f.nome.trim()) return "Inserisci un nome.";
    if (!emailRx.test(String(f.email || "").trim())) return "Email non valida.";
    if (String(f.home_cap || "").trim() && String(f.home_cap).trim().length !== 5) {
      return "Il CAP deve contenere 5 cifre.";
    }
    const lat = toDot(f.home_lat);
    const lng = toDot(f.home_lng);
    if (lat !== null && isNaN(Number(lat))) return "Latitudine non valida.";
    if (lng !== null && isNaN(Number(lng))) return "Longitudine non valida.";
    if (lat !== null && (Number(lat) < -90 || Number(lat) > 90)) return "Latitudine fuori range (-90/+90).";
    if (lng !== null && (Number(lng) < -180 || Number(lng) > 180)) return "Longitudine fuori range (-180/+180).";
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErr("");

      // 1) payload base
      const payload = {
        ...form,
        zona_id: Number(form.zona_id) || 0,
        attivo: Number(form.attivo) ? 1 : 0,
      };

      // 2) normalizza campi "casa" + date profilo
      const street = String(form.home_street || "").trim();
      const cap    = String(form.home_cap || "").trim();
      const city   = String(form.home_city || "").trim();
      // se l’utente non ha usato i campi separati, usa il vecchio “home_address”
      const composed = [street, cap, city].filter(Boolean).join(" ");
      payload.home_address = (composed || String(form.home_address || "").trim() || null);
      payload.home_lat = toDot(form.home_lat);
      payload.home_lng = toDot(form.home_lng);

      if (payload.profilo) {
        payload.profilo = {
          ...payload.profilo,
          data_assunzione: toIsoDate(payload.profilo.data_assunzione),
          data_scadenza_contratto: toIsoDate(payload.profilo.data_scadenza_contratto),
        };
      }

      // 3) in modifica la password è opzionale
      if (!payload.password) delete payload.password;

      // 4) invia profilo solo se c'è almeno un campo valorizzato
      const prof = payload.profilo || {};
      const hasProfile = Object.values(prof).some(v => String(v ?? '').trim() !== '');
      if (!hasProfile) {
        delete payload.profilo;
      }

      // VALIDAZIONE lato client (dopo normalizzazione)
      const vErr = validateForm(payload);
      if (vErr) throw new Error(vErr);

      // 5) POST principale
      await apiPost(SAVE_URL, payload);

      // 6) Rinforzo: aggiorna comunque i 3 campi casa con l'API minimal (che sappiamo funzionare)
      try {
        await apiPost(SAVE_HOME_URL, {
          id: payload.id || form.id,
          home_address: payload.home_address || null,
          home_lat: payload.home_lat ?? null,
          home_lng: payload.home_lng ?? null,
        });
      } catch (e) {
        console.warn("users_save_home fallback:", e?.message || e);
      }

      setOpen(false);
      await reload();
      alert("Utente salvato correttamente.");
    } catch (e2) {
      setErr(e2.message || "Errore salvataggio");
      alert(e2.message || "Errore salvataggio");
    } finally {
      setLoading(false);
    }
  };

  /* delete */
  const apiDelete = async (user, hard=false)=>{
    const msg = hard
      ? `Eliminare DEFINITIVAMENTE “${user.nome}”?`
      : `Disattivare “${user.nome}”?`;
    if(!window.confirm(msg)) return;
    try{
      const r = await fetch(DEL_URL,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        credentials:"same-origin",
        body: JSON.stringify({ id:Number(user.id), hard: hard?1:0 })
      });
      const j = await r.json().catch(()=>({}));
      if(!r.ok || j.status!=="OK"){
        if(r.status===409 && j.refs){
          const lines = Object.entries(j.refs).map(([k,v])=>`- ${k}: ${v}`).join("\n");
          throw new Error((j.message||"Impossibile eliminare")+"\n\n"+lines);
        }
        throw new Error(j.message||`HTTP ${r.status}`);
      }
      await reload();
    }catch(e){
      alert(e.message || String(e));
    }
  };

  /* attiva/disattiva da lista */
  const toggleActive = async (u) => {
    try {
      const res = await apiPost(SAVE_URL, {
        id: Number(u.id),
        attivo: Number(u.attivo) ? 0 : 1,
      });
      if (res.status && res.status !== "OK") throw new Error(res.message || "Errore aggiornamento");
      await reload();
    } catch (e) {
      alert(e.message || String(e));
    }
  };

  const geocodeHome = async () => {
    const addr = (
      [form.home_street, form.home_cap, form.home_city].map(v => String(v||'').trim()).filter(Boolean).join(' ')
    ) || String(form.home_address || '').trim();
    if (!addr) { alert('Inserisci prima un indirizzo di partenza.'); return; }
    try {
      setLoading(true);
      const r = await fetch(GEOCODE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ address: addr })
      });
      const j = await r.json().catch(()=> ({}));
      if (!r.ok || j.status !== 'OK') throw new Error(j.message || `HTTP ${r.status}`);
      setForm(f => ({
        ...f,
        home_lat: j.lat != null ? String(j.lat) : '',
        home_lng: j.lng != null ? String(j.lng) : '',
        home_address: addr  // memorizzo anche la stringa composta
      }));
    } catch (e) {
      alert(e.message || 'Geocoding fallito');
    } finally {
      setLoading(false);
    }
  };

  const wantsProfile = form.ruolo==="rilevatore" || form.ruolo==="senior_posa" || form.ruolo==="senior";

  const clearFilters = () => { setRole(""); setZone(0); setQ(""); };
  const exportCsv = () => {
    try{
      const csv = toCsv(sorted);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `utenti_${todayYMD()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }catch(e){
      alert(e.message || "Esporta CSV fallita");
    }
  };

  /* ============== RENDER ============== */
  return (
    <div className="page-container" style={{ padding: 18 }}>
      {/* HERO */}
      <div style={S.heroWrap}>
        <div style={S.hero}>
          <div>
            <h2 style={{ margin: 0, letterSpacing:.2 }}>Gestione Utenti</h2>
            <div style={{ opacity:.8, fontSize:12, marginTop:4 }}>
              filtra, assegna ruoli e zone · scorciatoie: <b>/</b> cerca · <b>n</b> nuovo
            </div>
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button style={S.ghostBtn} onClick={exportCsv} title="Esporta CSV (filtrati/ordinati)">⬇︎ Esporta CSV</button>
            <button style={S.primaryBtn} onClick={openNew}>＋ Nuovo</button>
          </div>
        </div>
      </div>

      {/* FILTERS */}
      <div style={S.filters}>
        <div style={S.segment} role="tablist" aria-label="Filtro ruolo">
          {ROLES.map((r)=>(
            <button
              key={r.value||"all"}
              onClick={()=>setRole(r.value)}
              className={role===r.value?"seg active":"seg"}
              style={role===r.value?S.segActive:S.segBtn}
              type="button"
              role="tab"
              aria-selected={role===r.value}
            >
              {r.label}
            </button>
          ))}
        </div>

        <label style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
          <span className="sr-only" style={{ position:"absolute", left:-9999 }}>Zona</span>
          <select value={zone} onChange={(e)=>setZone(Number(e.target.value))} style={S.pill}>
            <option value={0}>Tutte le zone</option>
            {ZONE.map(z=><option key={z.id} value={z.id}>{z.label}</option>)}
          </select>
        </label>

        <div style={S.searchBox}>
          <span style={S.searchIcon}>⌕</span>
          <input
            ref={searchRef}
            placeholder="Cerca nome / email / telefono / indirizzo…"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            style={S.searchInput}
            aria-label="Cerca"
          />
        </div>

        <button onClick={reload} disabled={loading} style={S.ghostBtn} title="Ricarica elenco">
          {loading ? "…" : "Ricarica"}
        </button>

        <button onClick={clearFilters} style={S.ghostBtn} title="Pulisci filtri">
          Pulisci filtri
        </button>

        <div style={{ flex: 1 }} />

        <div style={{ opacity:.7, fontSize:12 }}>
          {sorted.length} risultati
        </div>
      </div>

      {err && <div style={S.alertErr}>{err}</div>}

      {/* TABLE */}
      <div style={S.tableWrap}>
        <table style={S.table} className="neo-table">
          <thead>
            <tr>
              <Th label="#"     sortKey="id"     sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
              <Th label="Utente" sortKey="nome"   sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
              <Th label="Email"  sortKey="email"  sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
              <Th label="Ruolo"  sortKey="ruolo"  sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
              <Th label="Zona"   sortKey="zona"   sortBy={sortBy} sortDir={sortDir} onSort={setSort} />
              <Th label="Attivo" sortKey="attivo" sortBy={sortBy} sortDir={sortDir} onSort={setSort} thStyle={{ textAlign:"center" }} />
              <th style={{ width: 360 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r)=> {
              const roleSlug = String(r.ruolo||"").toLowerCase();
              const c = ROLE_COLORS[roleSlug] || ROLE_COLORS.default;
              return (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ ...S.avatar, background: c.soft, color: c.bg }}>
                        {initials(r.nome)}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {/* Nome */}
                        <div style={{ fontWeight: 700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {r.nome || "—"}
                        </div>
                        {/* Riga meta: ID · Telefono · Email */}
                        <div style={S.metaRow}>
                          <span style={S.metaChip}>#{r.id}</span>
                          {r.profilo?.telefono && (
                            <span style={S.metaChip}>📞 {String(r.profilo.telefono).trim()}</span>
                          )}
                          {r.email && (
                            <span style={S.metaChip} title={r.email}>✉️ {r.email}</span>
                          )}
                        </div>
                        {/* Indirizzo in piccolo + lat/lng */}
                        <div style={S.addrRow} title={r.home_address || ""}>
                          <span style={{ opacity:.75, marginRight:6 }}>📍</span>
                          <span style={S.addrText}>
                            {r.home_address || "—"}
                          </span>
                          {(r.home_lat ?? "") !== "" && (r.home_lng ?? "") !== "" && (
                            <span style={{ ...S.metaChip, marginLeft:6 }}>
                              {Number(r.home_lat).toFixed(5)}, {Number(r.home_lng).toFixed(5)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{r.email}</td>
                  <td>
                    <span
                      style={{
                        ...S.roleChip,
                        background: `${c.bg}14`,
                        color: c.bg,
                        border:`1px solid ${c.bg}33`
                      }}
                    >
                      {roleLabel(roleSlug)}
                    </span>
                  </td>
                  <td>
                    <span style={S.zoneChip}>{r.zona_nome || zLabel(r.zona_id)}</span>
                  </td>
                  <td style={{ textAlign:"center" }}>
                    <button
                      onClick={()=>toggleActive(r)}
                      title="Attiva/Disattiva"
                      style={{
                        ...S.toggle,
                        background: Number(r.attivo)? "#dcfce7":"#fee2e2",
                        color: Number(r.attivo)? "#166534":"#991b1b",
                        borderColor: Number(r.attivo)? "#86efac":"#fecaca"
                      }}
                    >
                      {Number(r.attivo) ? "Sì" : "No"}
                    </button>
                  </td>
                  <td style={{ whiteSpace:"nowrap" }}>
                    <button style={S.smallBtn} onClick={()=>openEdit(r)}>Modifica</button>{" "}
                    <button style={S.smallBtn} onClick={()=>apiDelete(r,false)}>Disattiva</button>{" "}
                    <button
                      style={{ ...S.smallBtn, color:"#b91c1c", borderColor:"#fecaca" }}
                      onClick={()=>apiDelete(r,true)}
                      title="Elimina definitivamente"
                    >
                      Elimina
                    </button>
                  </td>
                </tr>
              );
            })}
            {!sorted.length && (
              <tr>
                <td colSpan={7} style={{ textAlign:"center", opacity:.65, padding:22 }}>
                  Nessun utente.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODAL */}
      <Modal
        open={open}
        onClose={()=>setOpen(false)}
        title={form.id ? `Modifica utente #${form.id}` : "Nuovo utente"}
      >
        {/* Il primo campo con data-autofocus verrà messo a fuoco automaticamente */}
        <form onSubmit={onSubmit} style={S.grid4}>
          <div>
            <label>Nome</label>
            <input
              style={S.inp}
              value={form.nome}
              onChange={e=>setForm({ ...form, nome:e.target.value })}
              required
              data-autofocus
            />
          </div>

          <div>
            <label>Email</label>
            <input
              style={S.inp}
              type="email"
              value={form.email}
              onChange={e=>setForm({ ...form, email:e.target.value })}
              required
            />
          </div>

          <div>
            <label style={S.labelSm}>
              Password {form.id ? "(vuoto per non cambiare)" : ""}
            </label>
            <input
              style={S.inp}
              type="text"
              value={form.password}
              onChange={(e)=>setForm({ ...form, password: e.target.value })}
              autoComplete="off"
            />
            {form.id ? (
              <div style={{ fontSize: 9, opacity: .65, marginTop: 4 }}>
                In modifica: lascia vuoto per non cambiare la password.
              </div>
            ) : null}
          </div>

          <div>
            <label>Ruolo</label>
            <select
              style={S.inp}
              value={form.ruolo}
              onChange={e=>setForm({ ...form, ruolo:e.target.value })}
            >
              {ROLES.filter(r=>r.value!=="").map(r=> (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label>Zona</label>
            <select
              style={S.inp}
              value={form.zona_id}
              onChange={e=>setForm({ ...form, zona_id:Number(e.target.value) })}
            >
              <option value={0}>—</option>
              {ZONE.map(z=> <option key={z.id} value={z.id}>{z.label}</option>)}
            </select>
          </div>

          <div>
            <label>Attivo</label>
            <select
              style={S.inp}
              value={form.attivo}
              onChange={e=>setForm({ ...form, attivo:Number(e.target.value) })}
            >
              <option value={1}>Sì</option>
              <option value={0}>No</option>
            </select>
          </div>

          {/* ===== Punto di partenza (casa) ===== */}
          <div style={{ gridColumn: "1 / span 4", marginTop: 6, fontWeight: 800 }}>
            Dati di partenza (casa)
          </div>

          <div style={{ gridColumn: "1 / span 4" }}>
            {/* riga: Via/Civico (larga) · CAP (stretta) · Comune (larga) · bottone */}
            <div
              style={{
                display:"grid",
                gridTemplateColumns:"2.2fr 0.8fr 1.6fr auto",
                gap: 8
              }}
            >
              <input
                style={S.inp}
                value={form.home_street}
                onChange={(e)=>setForm({ ...form, home_street: e.target.value })}
                placeholder="Via / Civico"
                autoComplete="street-address"
              />
              <input
                style={S.inp}
                value={form.home_cap}
                onChange={(e)=>setForm({ ...form, home_cap: e.target.value.replace(/\D/g,'').slice(0,5) })}
                inputMode="numeric"
                placeholder="CAP"
                autoComplete="postal-code"
              />
              <input
                style={S.inp}
                value={form.home_city}
                onChange={(e)=>setForm({ ...form, home_city: e.target.value })}
                placeholder="Comune"
                autoComplete="address-level2"
              />
              <button
                type="button"
                style={S.smallBtn}
                onClick={geocodeHome}
                title="Geocodifica indirizzo"
                disabled={
                  loading ||
                  !([form.home_street, form.home_cap, form.home_city, form.home_address]
                    .some(v => String(v||'').trim()))
                }
              >
                📍 Geocodifica
              </button>
            </div>
            <div style={{ fontSize:10, opacity:.6, marginTop:4 }}>
              Puoi compilare Via/Civico, CAP e Comune: al salvataggio verranno uniti in “Indirizzo di partenza”.
            </div>
          </div>

          <div>
            <label>Latitudine</label>
            <input
              style={S.inp}
              type="number"
              step="0.000001"
              value={form.home_lat === "" || form.home_lat === null ? "" : String(form.home_lat)}
              onChange={(e)=>setForm({ ...form, home_lat: e.target.value })}
              placeholder="es. 45.438384"
              inputMode="decimal"
            />
          </div>

          <div>
            <label>Longitudine</label>
            <input
              style={S.inp}
              type="number"
              step="0.000001"
              value={form.home_lng === "" || form.home_lng === null ? "" : String(form.home_lng)}
              onChange={(e)=>setForm({ ...form, home_lng: e.target.value })}
              placeholder="es. 10.991622"
              inputMode="decimal"
            />
          </div>

          {/* spacer */}
          <div style={{ gridColumn:"span 2" }} />

          {wantsProfile && (
            <>
              <div style={{ gridColumn:"1 / span 4", marginTop: 6, fontWeight:800 }}>
                Profilo {form.ruolo}
              </div>

              <div>
                <label>Telefono</label>
                <input
                  style={S.inp}
                  value={form.profilo.telefono}
                  onChange={e=>setForm({ ...form, profilo:{ ...form.profilo, telefono:e.target.value }})}
                />
              </div>

              <div>
                <label>Foto (URL)</label>
                <input
                  style={S.inp}
                  value={form.profilo.foto}
                  onChange={e=>setForm({ ...form, profilo:{ ...form.profilo, foto:e.target.value }})}
                />
              </div>

              <div>
                <label>Data assunzione</label>
                <input
                  style={S.inp}
                  type="date"
                  max={todayYMD()}
                  value={form.profilo.data_assunzione || ""}
                  onChange={(e)=>setForm({ ...form, profilo:{ ...form.profilo, data_assunzione:e.target.value }})}
                />
              </div>

              <div>
                <label>Scadenza contratto</label>
                <input
                  style={S.inp}
                  type="date"
                  value={form.profilo.data_scadenza_contratto || ""}
                  onChange={(e)=>setForm({ ...form, profilo:{ ...form.profilo, data_scadenza_contratto:e.target.value }})}
                />
              </div>

              <div>
                <label>Targa auto</label>
                <input
                  style={S.inp}
                  value={form.profilo.auto_targa}
                  onChange={e=>setForm({ ...form, profilo:{ ...form.profilo, auto_targa:e.target.value.toUpperCase() }})}
                />
              </div>

              <div>
                <label>Computer</label>
                <input
                  style={S.inp}
                  value={form.profilo.computer}
                  onChange={e=>setForm({ ...form, profilo:{ ...form.profilo, computer:e.target.value }})}
                />
              </div>

              <div>
                <label>Email personale</label>
                <input
                  style={S.inp}
                  type="email"
                  value={form.profilo.email_personale}
                  onChange={(e)=>setForm({ ...form, profilo:{ ...form.profilo, email_personale:e.target.value }})}
                />
              </div>

              <div>
                <label>Plaud assegnato</label>
                <input
                  style={S.inp}
                  value={form.profilo.plaud_assegnato}
                  onChange={(e)=>setForm({ ...form, profilo:{ ...form.profilo, plaud_assegnato:e.target.value }})}
                />
              </div>

              <div style={{ gridColumn:"1 / span 4" }}>
                <label>Note</label>
                <textarea
                  rows={3}
                  style={{ ...S.inp, minHeight:90, resize:"vertical" }}
                  value={form.profilo.note}
                  onChange={(e)=>setForm({ ...form, profilo:{ ...form.profilo, note:e.target.value }})}
                />
              </div>
            </>
          )}

          <div style={{ gridColumn:"1 / span 4", textAlign:"right", marginTop: 6 }}>
            <button
              type="button"
              onClick={()=>setOpen(false)}
              style={{ ...S.ghostBtn, marginRight: 8 }}
            >
              Annulla
            </button>
            <button type="submit" disabled={loading} style={S.primaryBtn}>
              {loading ? "Salvo…" : "Salva"}
            </button>
          </div>
        </form>
      </Modal>

      {/* FAB */}
      <button aria-label="Nuovo utente" onClick={openNew} style={S.fab}>＋</button>

      {/* CSS extra */}
      <style>{`
        .neo-table th, .neo-table td { padding: 11px 12px; }
        .neo-table thead th {
          position: sticky; top: 0; z-index: 1;
          background: rgba(255,255,255,.85); backdrop-filter: blur(6px);
          border-bottom: 1px solid #e5e7eb;
          user-select: none;
        }
        .neo-table tbody tr:nth-child(odd) { background: rgba(248,250,252,.65); }
        .neo-table tbody tr { transition: transform .06s ease, background .15s ease; }
        .neo-table tbody tr:hover { background: #f1f5f9; transform: translateY(-1px); }

        @keyframes pop {
          from { transform: scale(.98); opacity: .0; }
          to   { transform: scale(1);   opacity: 1;  }
        }
      `}</style>
    </div>
  );
}

/* =============== TH ORDINABILE =============== */
function Th({ label, sortKey, sortBy, sortDir, onSort, thStyle }) {
  const active = sortBy === sortKey;
  const arrow = active ? (sortDir === "asc" ? "▲" : "▼") : "↕";
  return (
    <th
      onClick={()=> onSort(sortKey)}
      style={{ cursor:"pointer", whiteSpace:"nowrap", ...thStyle }}
      title="Clic per ordinare"
    >
      <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}>
        {label} <span style={{ opacity:.6, fontSize:12 }}>{arrow}</span>
      </span>
    </th>
  );
}

/* =============== MODEL =============== */
function makeEmptyForm(){
  return {
    id: 0,
    nome: "",
    email: "",
    password: "",
    ruolo: "centralino",
    zona_id: 0,
    attivo: 1,
    // Punto di partenza (casa)
    home_address: "",   // stringa “completa” (verrà composta alla submit)
    home_street: "",    // Via/civico
    home_cap: "",       // CAP (5 cifre)
    home_city: "",      // Comune
    home_lat: "",
    home_lng: "",
    profilo: {
      telefono: "",
      foto: "",
      data_assunzione: "",
      data_scadenza_contratto: "",
      auto_targa: "",
      computer: "",
      email_personale: "",
      plaud_assegnato: "",
      note: "",
    },
  };
}

/* =============== STILI =============== */
const S = {
  heroWrap: {
    position: "sticky",
    top: -8,
    zIndex: 10,
    background: "linear-gradient(180deg,#ffffff, #ffffffa6 60%, #ffffff00)",
    backdropFilter: "saturate(160%) blur(2px)",
    borderBottom: "1px solid #eef2f7",
    marginBottom: 10,
  },
  hero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 6px",
  },
  labelSm: {
    fontSize: 12,
    lineHeight: 1.1,
    fontWeight: 700,
    display: "inline-block",
    marginBottom: 4,
    opacity: 0.75,
  },
  filters: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    padding: 12,
    borderRadius: 14,
    background: "linear-gradient(180deg,#fff,#fafafa)",
    border: "1px solid #eef2f7",
    boxShadow: "0 10px 30px rgba(2,6,23,.06)",
    marginBottom: 12,
  },
  segment: {
    display: "inline-flex",
    padding: 4,
    borderRadius: 999,
    background: "#f1f5f9",
    border: "1px solid #e2e8f0",
  },
  segBtn: {
    padding: "8px 12px",
    border: "none",
    cursor: "pointer",
    background: "transparent",
    borderRadius: 999,
    fontWeight: 700,
    color: "#475569",
  },
  segActive: {
    padding: "8px 12px",
    border: "none",
    cursor: "pointer",
    background: "#fff",
    borderRadius: 999,
    fontWeight: 800,
    color: "#0f172a",
    boxShadow: "0 2px 8px rgba(0,0,0,.06)",
  },
  pill: {
    padding: "9px 14px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#ffffff",
    fontWeight: 700,
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    background: "#fff",
    minWidth: 280,
  },
  searchIcon: { fontSize: 14, opacity: 0.6 },
  searchInput: { border: "none", outline: "none", width: "100%", fontSize: 14 },
  tableWrap: {
    borderRadius: 14,
    overflow: "auto",
    boxShadow: "0 10px 30px rgba(2,6,23,.06)",
    border: "1px solid #eef2f7",
    background: "linear-gradient(180deg,#ffffff,#fcfcff)",
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  roleChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
  },
  zoneChip: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    background: "#f1f5f9",
    border: "1px solid #e5e7eb",
    fontWeight: 700,
    fontSize: 12,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
    boxShadow: "0 2px 8px rgba(0,0,0,.06)",
  },
  toggle: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid transparent",
    fontWeight: 800,
    cursor: "pointer",
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "1px solid #b91c1c",
    background: "linear-gradient(180deg,#ef4444,#dc2626)",
    color: "#fff",
    fontWeight: 800,
  },
  ghostBtn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },
  smallBtn: {
    padding: "7px 10px",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },
  alertErr: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #fecaca",
    background: "#fee2e2",
    color: "#991b1b",
    marginBottom: 12,
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 18,
  },
  modal: {
    background: "#fff",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 22px 60px rgba(0,0,0,.35)",
    animation: "pop .14s ease-out",
  },
  modalHead: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "linear-gradient(180deg,#fef2f2,#fff)",
    borderBottom: "1px solid #f1f5f9",
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "center",
    marginTop: 2,
    marginBottom: 2,
  },
  metaChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    maxWidth: 220,
    padding: "2px 6px",
    borderRadius: 999,
    border: "1px solid #e5e7eb",
    background: "#f8fafc",
    fontSize: 11,
    lineHeight: 1.2,
    color: "#334155",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  addrRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 2,
  },
  addrText: {
    fontSize: 12,
    opacity: .7,
    maxWidth: 260,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  iconBtn: { border: "none", background: "transparent", fontSize: 24, cursor: "pointer", lineHeight: 1 },
  grid4: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 },
  inp: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #e5e7eb", outline: "none", background: "#fff" },
  fab: {
    position: "fixed",
    right: 22,
    bottom: 22,
    zIndex: 30,
    width: 56,
    height: 56,
    borderRadius: 16,
    border: "1px solid #b91c1c",
    background: "linear-gradient(180deg,#ef4444,#dc2626)",
    color: "#fff",
    fontSize: 26,
    fontWeight: 800,
    display: "grid",
    placeItems: "center",
    boxShadow: "0 10px 24px rgba(239,68,68,.35)",
    cursor: "pointer",
  },
};
